import { useCallback, useEffect, useRef, useState } from 'react'
import type { EditorMode } from '../components/ModeToggle'

/**
 * useDocCanvasMorph — orchestrates the Doc ↔ Canvas verwandlung.
 *
 * Animation timing matches ArkturianClaude's spec (IACP 97a49444):
 *
 *   Doc → Canvas (800ms, ease-out at the spatial layer):
 *     0-100ms   Initiation     — viewport scale starts shrinking
 *     100-300ms Decohesion     — linear order breaks; width/height collapse
 *     300-600ms Spatial Arr.   — X/Y interpolate to canvas-meta positions
 *     500-800ms Edge-Materialz. (overlapping) — opacity 0→1 staggered by confidence
 *     750-800ms Settle         — focusGlow sine-pulse starts
 *
 *   Canvas → Doc (500ms, asymmetric — re-linearisation is a read-op):
 *     0-150ms   Edge-Dissolve  — opacity → 0, glow stops
 *     100-400ms Collapse       — X → 0, Y → linear-stacked
 *     300-500ms Reform         — width → 100%, block_type CSS reactivates
 *     450-500ms Cursor-Settle
 *
 * The hook publishes a `progress` value in 0..1 plus a `direction`
 * (`'opening'` | `'closing'` | `'idle'`) that CanvasView reads to
 * compute per-phase interpolation factors. Card positions, edge
 * opacity, focusGlow amplitude all derive from this single source of
 * truth — same Engine-philosophy as canvas-engine's InterpolatedProperty
 * pool: one master signal, all derived state animates from it.
 */

const OPEN_DURATION_MS = 800
const CLOSE_DURATION_MS = 500

export type MorphDirection = 'opening' | 'closing' | 'idle'

export interface MorphState {
  /** 0 = full doc-mode, 1 = full canvas-mode, intermediates during animation. */
  progress: number
  direction: MorphDirection
  /** Hint for consumers: the user-intent target mode. */
  targetMode: EditorMode
  /** Phase 1 factor 0..1 — Initiation / Edge-Dissolve. */
  phase1: number
  /** Phase 2 factor 0..1 — Decohesion / Collapse. */
  phase2: number
  /** Phase 3 factor 0..1 — Spatial Arrangement / Reform. */
  phase3: number
  /** Phase 4 factor 0..1 — Edge-Materialization / Cursor-Settle. */
  phase4: number
}

const IDLE_DOC: MorphState = {
  progress: 0,
  direction: 'idle',
  targetMode: 'doc',
  phase1: 0,
  phase2: 0,
  phase3: 0,
  phase4: 0,
}

const IDLE_CANVAS: MorphState = {
  progress: 1,
  direction: 'idle',
  targetMode: 'canvas',
  phase1: 1,
  phase2: 1,
  phase3: 1,
  phase4: 1,
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/**
 * Map a 0..1 master progress to per-phase factors per Claude's
 * Frame-by-Frame timing. Phases overlap by design.
 */
function computePhases(
  progress: number,
  direction: MorphDirection,
): Pick<MorphState, 'phase1' | 'phase2' | 'phase3' | 'phase4'> {
  if (direction === 'opening') {
    // Doc → Canvas, 800ms total. progress maps 0→1 over 800ms.
    // Phase 1: 0-100ms     → progress 0.000-0.125
    // Phase 2: 100-300ms   → progress 0.125-0.375
    // Phase 3: 300-600ms   → progress 0.375-0.750
    // Phase 4: 500-800ms   → progress 0.625-1.000 (overlaps Phase 3)
    return {
      phase1: easeOutCubic(clamp01((progress - 0.0) / 0.125)),
      phase2: easeOutCubic(clamp01((progress - 0.125) / 0.25)),
      phase3: easeInOutCubic(clamp01((progress - 0.375) / 0.375)),
      phase4: easeOutCubic(clamp01((progress - 0.625) / 0.375)),
    }
  }
  if (direction === 'closing') {
    // Canvas → Doc, 500ms total. progress maps 1→0 over 500ms.
    // For consumers, phases describe the "doc-restoration" amount.
    // Phase 1: 0-150ms     → (1-progress) 0.0-0.3   Edge-Dissolve
    // Phase 2: 100-400ms   → (1-progress) 0.2-0.8   Collapse
    // Phase 3: 300-500ms   → (1-progress) 0.6-1.0   Reform
    // Phase 4: 450-500ms   → (1-progress) 0.9-1.0   Cursor-Settle
    const inv = 1 - progress
    return {
      phase1: easeOutCubic(clamp01((inv - 0.0) / 0.3)),
      phase2: easeOutCubic(clamp01((inv - 0.2) / 0.6)),
      phase3: easeOutCubic(clamp01((inv - 0.6) / 0.4)),
      phase4: easeOutCubic(clamp01((inv - 0.9) / 0.1)),
    }
  }
  if (progress <= 0.001) {
    return { phase1: 0, phase2: 0, phase3: 0, phase4: 0 }
  }
  return { phase1: 1, phase2: 1, phase3: 1, phase4: 1 }
}

export interface UseDocCanvasMorphResult {
  state: MorphState
  /** Trigger an opening animation (Doc → Canvas). No-op if already canvas. */
  openCanvas: () => void
  /** Trigger a closing animation (Canvas → Doc). No-op if already doc. */
  closeCanvas: () => void
  /** Toggle whichever direction makes sense given current state. */
  toggle: () => void
}

export function useDocCanvasMorph(
  initialMode: EditorMode = 'doc',
): UseDocCanvasMorphResult {
  const [state, setState] = useState<MorphState>(
    initialMode === 'canvas' ? IDLE_CANVAS : IDLE_DOC,
  )
  const rafRef = useRef<number | null>(null)
  const startTsRef = useRef<number | null>(null)
  const startProgressRef = useRef<number>(0)
  const directionRef = useRef<MorphDirection>('idle')

  const cancelRaf = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const animate = useCallback(
    (direction: 'opening' | 'closing', targetMode: EditorMode) => {
      cancelRaf()
      directionRef.current = direction
      startTsRef.current = null
      // Capture current progress so an interrupted animation reverses
      // smoothly instead of snapping.
      setState((prev) => {
        startProgressRef.current = prev.progress
        return { ...prev, direction, targetMode }
      })

      const duration =
        direction === 'opening' ? OPEN_DURATION_MS : CLOSE_DURATION_MS
      const startProgress = startProgressRef.current
      const endProgress = direction === 'opening' ? 1 : 0

      const tick = (ts: number) => {
        if (startTsRef.current == null) startTsRef.current = ts
        const elapsed = ts - startTsRef.current
        const t = clamp01(elapsed / duration)
        const progress =
          startProgress + (endProgress - startProgress) * t
        const phases = computePhases(progress, direction)
        setState({
          progress,
          direction,
          targetMode,
          ...phases,
        })
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick)
        } else {
          rafRef.current = null
          directionRef.current = 'idle'
          setState(direction === 'opening' ? IDLE_CANVAS : IDLE_DOC)
        }
      }
      rafRef.current = requestAnimationFrame(tick)
    },
    [cancelRaf],
  )

  const openCanvas = useCallback(() => {
    if (state.targetMode === 'canvas' && state.direction === 'idle') return
    animate('opening', 'canvas')
  }, [animate, state.targetMode, state.direction])

  const closeCanvas = useCallback(() => {
    if (state.targetMode === 'doc' && state.direction === 'idle') return
    animate('closing', 'doc')
  }, [animate, state.targetMode, state.direction])

  const toggle = useCallback(() => {
    if (state.targetMode === 'canvas') closeCanvas()
    else openCanvas()
  }, [state.targetMode, openCanvas, closeCanvas])

  useEffect(() => () => cancelRaf(), [cancelRaf])

  return { state, openCanvas, closeCanvas, toggle }
}
